import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Grid,
    Heading,
    HStack,
    Icon,
    Progress,
} from '@chakra-ui/react';
import { useServicesDashboard } from 'context/ServicesDashboardContext';
import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import IconPlatform from './IconPlatform';
import {
    ACS,
    APP_AUTH,
    APP_AUTH_GCCH,
    DIRECT_ROUTING,
    EIGHTBYEIGHT,
    MICROSOFT_GRAPH,
    OPERATOR_CONNECT,
    SBC,
    SRT,
    TPM,
    VENDOR_DEFINED,
    ZOOM,
    ZOOM_AUTH,
    statusPriorityOrder,
    GCC_HIGH,
    WEBEX,
    WEBEX_AUTH,
    OPERATOR_PLATFORM,
} from '../constants';
import { PlatformEnums } from 'utils/platforms';
import { getSimpleRedirectAuthServiceTypeByAppAuthTypeID } from 'services/platformRegistry';
import { accordionCategory } from './AccordionCategory';
import DashboardCard from './DashboardCard';
import SBCCardDetails from './SBCCardDetails';
import SRTCard from 'pages/services/new/ServiceDashboard/Services/SRT';
import TPMCard from './TPM/TPMCard';
import VendorCard from './VendorCard/Vendor/VendorCard';
import AppAuthCard from './AppAuthCard';
import { useDashboardMenu } from 'context/DashboardMenuContext';
import { GetCardStatus } from './helpers/cardStatusHelper';
import DashboardHydrationCardSkeleton from './DashboardHydrationCardSkeleton';
import { useServiceDashboardBulk } from 'hooks/useCustomQuery/useServiceDashboard';

const DASHBOARD_ACCORDION_STATE_CACHE_VERSION = 1;
const DASHBOARD_ACCORDION_STATE_CACHE_PREFIX = 'dashboard-v5-accordion-state';
const DASHBOARD_ACCORDION_STATE_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30;

// The Microsoft tile always loads expanded; every other category collapses by
// default unless a service needs attention. Matches accordionCategory title.
const MICROSOFT_CATEGORY_TITLE = 'Microsoft';
const ATTENTION_STATUS_COLORS = ['yellow', 'red'];

const getDashboardAccordionStateCacheKey = (companyID, userEmail) => {
    const normalizedUser = (userEmail || 'anonymous').toLowerCase();

    return `${DASHBOARD_ACCORDION_STATE_CACHE_PREFIX}:v${DASHBOARD_ACCORDION_STATE_CACHE_VERSION}:${normalizedUser}:${companyID}`;
};

const readDashboardAccordionStateCache = (companyID, userEmail) => {
    if (typeof window === 'undefined' || !companyID) {
        return null;
    }

    try {
        const cachedValue = window.localStorage.getItem(
            getDashboardAccordionStateCacheKey(companyID, userEmail),
        );
        if (!cachedValue) return null;

        const parsedCache = JSON.parse(cachedValue);
        if (parsedCache?.version !== DASHBOARD_ACCORDION_STATE_CACHE_VERSION) {
            return null;
        }
        if (
            typeof parsedCache?.updatedAt !== 'number' ||
            Date.now() - parsedCache.updatedAt >
                DASHBOARD_ACCORDION_STATE_CACHE_MAX_AGE_MS
        ) {
            return null;
        }

        const expandedTitles = Array.isArray(parsedCache?.expanded)
            ? parsedCache.expanded
            : [];

        return expandedTitles.filter(
            (title) => typeof title === 'string' && title.trim().length,
        );
    } catch (error) {
        return null;
    }
};

const writeDashboardAccordionStateCache = (companyID, userEmail, expanded) => {
    if (typeof window === 'undefined' || !companyID) {
        return;
    }

    try {
        window.localStorage.setItem(
            getDashboardAccordionStateCacheKey(companyID, userEmail),
            JSON.stringify({
                version: DASHBOARD_ACCORDION_STATE_CACHE_VERSION,
                updatedAt: Date.now(),
                expanded: Array.isArray(expanded) ? expanded : [],
            }),
        );
    } catch (error) {
        // Ignore local storage errors and fall back to default behavior.
    }
};

const mapExpandedTitlesToIndices = (expandedTitles, categoryTitles) => {
    if (!Array.isArray(expandedTitles)) return null;

    const expandedSet = new Set(expandedTitles);
    const indices = [];
    let matches = 0;

    categoryTitles.forEach((title, idx) => {
        if (expandedSet.has(title)) {
            indices.push(idx);
            matches += 1;
        }
    });

    // If we had stored titles but none match the current categories, treat
    // the cache as invalid (likely due to a category list change).
    if (expandedTitles.length > 0 && matches === 0) {
        return null;
    }

    return indices;
};

const DashboardAccordion = ({
    services = [],
    serviceWizard,
    companyData,
    companyAppAuth = [],
    msServiceAuth = {},
    AppAuthService,
    ZoomAppAuthService,
    EightByEightAppAuthService,
    RingCentralAppAuthService,
    DialPadAppAuthService,
    WebexAppAuthService,
    AppAuthServiceType,
    ZoomAppAuthServiceType,
    EightByEightAppAuthServiceType,
    RingCentralAppAuthServiceType,
    DialPadAppAuthServiceType,
    WebexAppAuthServiceType,
    GccHighAppAuthServiceType,
    GccHighAppAuthService,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const currentCompany = useSelector(
        (state) => state.navigationLists?.currentCompany,
    );
    const accordionStateUser = useSelector(
        (state) => state.login?.email || state.login?.user || '',
    );
    const serviceStatus = useServicesDashboard();
    const { isQueryDisabled, setIsQueryDisabled } = useDashboardMenu();
    const [expandedIndices, setExpandedIndices] = useState(null);
    const [cachedExpandedTitles, setCachedExpandedTitles] = useState(null);
    const previousHadSkeletonRef = useRef(false);
    const hasManualExpansionRef = useRef(false);
    const shouldWriteCacheFromDefaultRef = useRef(false);

    // User would need to manually refresh if idle for more than 2 minutes
    useEffect(() => {
        if (!isQueryDisabled) {
            const timer = setTimeout(() => {
                setIsQueryDisabled(true);
            }, 120000);

            return () => {
                clearTimeout(timer);
            };
        }

        // reset refresh on navigation
        return () => {
            setIsQueryDisabled(false);
        };
    }, [isQueryDisabled, setIsQueryDisabled]);

    // The consolidated /Services list payload omits the voice policy / dial
    // plan / region detail in statusDisplay for DR and GCC High, so those
    // cards get stuck on the generic Provisioning label. Only GET /Service/{id}
    // returns that detail, so fetch it per-service for just these two types.
    const drGcchServiceItems = useMemo(
        () =>
            services
                .filter(
                    (service) =>
                        !service?.__isSkeleton &&
                        (service?.serviceType === DIRECT_ROUTING ||
                            service?.serviceType === GCC_HIGH),
                )
                .map((service) => ({
                    serviceType: service.serviceType,
                    serviceId:
                        service?.id ?? service?.serviceId ?? service?.serviceID,
                }))
                .filter((item) => item.serviceId),
        [services],
    );

    useServiceDashboardBulk(drGcchServiceItems, { enabled: !isQueryDisabled });

    const getAuthServiceCardStatus = useCallback(
        (authServiceType, platformID) => {
            let statusKey = authServiceType;
            if (authServiceType === MICROSOFT_GRAPH) statusKey = APP_AUTH;
            // The GCC High Microsoft auth shares appAuthTypeID 'Microsoft'; it
            // is disambiguated by platformID and stored under APP_AUTH_GCCH.
            if (
                authServiceType === MICROSOFT_GRAPH &&
                platformID === PlatformEnums.GCC
            )
                statusKey = APP_AUTH_GCCH;
            // Simple-redirect platforms (EightByEight, RingCentral, DialPad,
            // Zoom, Webex) receive the API's appAuthTypeID here; translate it
            // to the dashboard status-context key it's stored under.
            statusKey =
                getSimpleRedirectAuthServiceTypeByAppAuthTypeID(
                    authServiceType,
                ) ?? statusKey;
            return serviceStatus[statusKey] || {};
        },
        [serviceStatus],
    );

    const groupedServices = useMemo(() => {
        const allServices = [
            {
                serviceType: AppAuthServiceType,
                service: AppAuthService,
                type: 'auth',
            },
            {
                serviceType: ZoomAppAuthServiceType,
                service: ZoomAppAuthService,
                type: 'auth',
            },
            {
                serviceType: EightByEightAppAuthServiceType,
                service: EightByEightAppAuthService,
                type: 'auth',
            },
            {
                serviceType: RingCentralAppAuthServiceType,
                service: RingCentralAppAuthService,
                type: 'auth',
            },
            {
                serviceType: DialPadAppAuthServiceType,
                service: DialPadAppAuthService,
                type: 'auth',
            },
            {
                serviceType: WebexAppAuthServiceType,
                service: WebexAppAuthService,
                type: 'auth',
            },
            {
                serviceType: GccHighAppAuthServiceType,
                service: GccHighAppAuthService,
                platformID: PlatformEnums.GCC,
                type: 'auth',
            },
            ...services.map((service) => ({ ...service, type: 'regular' })),
        ].filter((service) => service.serviceType);

        const grouped = allServices.reduce((acc, service) => {
            let category = 'Other';
            let categoryIcon = null;

            // Special handling for GCC High auth services - route to GCC High category
            if (
                service.type === 'auth' &&
                service.platformID === PlatformEnums.GCC
            ) {
                category = 'Microsoft Teams GCC High';
                categoryIcon = accordionCategory.find(
                    (c) => c.title === category,
                )?.icon;
            } else {
                // Special handling for Operator platform with specific platformId
                const effectiveServiceType =
                    service.serviceType === VENDOR_DEFINED &&
                    service.platformID === PlatformEnums.OPERATOR_PLATFORM
                        ? OPERATOR_PLATFORM
                        : service.serviceType;

                for (const categoryObj of accordionCategory) {
                    if (categoryObj.services.includes(effectiveServiceType)) {
                        category = categoryObj.title;
                        categoryIcon = categoryObj.icon;
                        break;
                    }
                }
            }

            if (!acc[category]) {
                acc[category] = {
                    services: [],
                    authServices: [],
                    icon: categoryIcon,
                };
            }

            if (
                service.type === 'auth' ||
                [ZOOM_AUTH, MICROSOFT_GRAPH, WEBEX_AUTH].includes(
                    service.serviceType,
                )
            ) {
                acc[category].authServices.push(service);
            } else {
                acc[category].services.push(service);
            }

            return acc;
        }, {});

        for (const category in grouped) {
            grouped[category].services.sort((a, b) => {
                const priorityServices = [
                    TPM,
                    DIRECT_ROUTING,
                    OPERATOR_CONNECT,
                    MICROSOFT_GRAPH,
                    EIGHTBYEIGHT,
                    ZOOM,
                    WEBEX,
                ];

                return (
                    priorityServices.indexOf(a.serviceType) -
                    priorityServices.indexOf(b.serviceType)
                );
            });
        }

        const categoryOrder = accordionCategory.map((cat) => cat.title);
        const sortedGroupedEntries = categoryOrder
            .filter(
                (categoryTitle) =>
                    grouped[categoryTitle] &&
                    (grouped[categoryTitle].services.length > 0 ||
                        grouped[categoryTitle].authServices.length > 0),
            )
            .map((categoryTitle) => [categoryTitle, grouped[categoryTitle]]);

        if (
            grouped['Other'] &&
            (grouped['Other'].services.length > 0 ||
                grouped['Other'].authServices.length > 0)
        ) {
            sortedGroupedEntries.push(['Other', grouped['Other']]);
        }
        const sortedGrouped = Object.fromEntries(sortedGroupedEntries);

        const finalGrouped = Object.entries(sortedGrouped).map(
            ([categoryTitle, accordionGroup]) => {
                const authCandidates = (accordionGroup.authServices || []).map(
                    (svc) => {
                        const authCard = getAuthServiceCardStatus(
                            svc.serviceType,
                            svc.platformID,
                        );
                        return authCard?.statusDisplay
                            ? {
                                  ...svc,
                                  statusDisplay: authCard.statusDisplay,
                                  statusLabel: authCard.statusLabel,
                                  color: authCard.color,
                                  animation: authCard.animation,
                                  hasStripes: authCard.hasStripes,
                              }
                            : svc;
                    },
                );

                const enrichedServices = (accordionGroup.services || []).map(
                    (svc) => {
                        const serviceCard = serviceStatus[svc.serviceType];

                        // Reducer status from a previously-viewed company is
                        // stale; keep the fresh list statusDisplay instead.
                        const isStale =
                            serviceCard?.serviceId !== undefined &&
                            serviceCard?.serviceId !== svc.id;

                        return serviceCard?.statusDisplay && !isStale
                            ? {
                                  ...svc,
                                  statusDisplay: serviceCard.statusDisplay,
                                  statusLabel: serviceCard.statusLabel,
                                  color: serviceCard.color,
                                  animation: serviceCard.animation,
                                  hasStripes: serviceCard.hasStripes,
                              }
                            : svc;
                    },
                );

                const candidates = [...enrichedServices, ...authCandidates];

                const { bestService } = candidates.reduce(
                    (acc, service) => {
                        const level =
                            service?.statusDisplay?.statusDisplayLevel;
                        if (level === undefined) return acc;

                        const idx = statusPriorityOrder.indexOf(level);
                        if (idx === -1) return acc;

                        if (idx < acc.bestIndex) {
                            return { bestIndex: idx, bestService: service };
                        }
                        return acc;
                    },
                    { bestIndex: Number.POSITIVE_INFINITY, bestService: null },
                );

                return [
                    categoryTitle,
                    {
                        ...accordionGroup,
                        // Fall back to the first card when no candidate has a
                        // statusDisplayLevel in statusPriorityOrder, so the
                        // category progress still reflects its service card
                        // (e.g. 8x8) instead of being undefined.
                        statusPriority: bestService ?? candidates[0] ?? null,
                    },
                ];
            },
        );
        return Object.fromEntries(finalGrouped);
    }, [
        AppAuthServiceType,
        AppAuthService,
        ZoomAppAuthServiceType,
        ZoomAppAuthService,
        EightByEightAppAuthServiceType,
        EightByEightAppAuthService,
        WebexAppAuthServiceType,
        WebexAppAuthService,
        GccHighAppAuthServiceType,
        GccHighAppAuthService,
        services,
        getAuthServiceCardStatus,
        RingCentralAppAuthService,
        RingCentralAppAuthServiceType,
        DialPadAppAuthService,
        DialPadAppAuthServiceType,
        serviceStatus,
    ]);

    const getServiceKey = (service, idx) => {
        const id = service?.serviceId ?? service?.serviceID ?? service?.id;
        if (id) return `${service?.serviceType}-${id}`;
        return `${service?.serviceType}-${idx}`;
    };

    // Resolve the app auth that belongs to a service. Commercial (OC/DR) and
    // GCC High services both use a Microsoft app auth (same appAuthTypeID
    // 'Microsoft'), so disambiguate by platformID — otherwise the first
    // Microsoft match wins and a commercial service can pair with the GCCH auth.
    const resolveServiceAppAuth = (service) => {
        if (!service?.appAuthType) return undefined;
        const expectedMsPlatform =
            service.serviceType === GCC_HIGH
                ? PlatformEnums.GCC
                : PlatformEnums.TEAMS;
        return companyAppAuth?.find((appAuth) => {
            if (!service.appAuthType.includes(appAuth.appAuthTypeID)) {
                return false;
            }
            if (appAuth.appAuthTypeID === MICROSOFT_GRAPH) {
                return appAuth.platformID === expectedMsPlatform;
            }
            return true;
        });
    };

    const renderServiceCard = (service, uniqueKey, animationDelayMs = 0) => {
        if (service?.__isSkeleton) {
            return (
                <DashboardHydrationCardSkeleton
                    key={uniqueKey}
                    service={service}
                    animationDelayMs={animationDelayMs}
                />
            );
        }

        switch (service.serviceType) {
            case TPM:
                return (
                    <TPMCard
                        key={uniqueKey}
                        service={service}
                        companyData={companyData}
                        serviceWizard={serviceWizard}
                    />
                );
            case ACS:
                return (
                    <DashboardCard
                        key={uniqueKey}
                        service={service}
                        companyData={companyData}
                    />
                );
            case OPERATOR_CONNECT:
                return (
                    <DashboardCard
                        key={uniqueKey}
                        service={service}
                        serviceWizard={serviceWizard}
                        msServiceAuth={msServiceAuth}
                        hasZoomAppAuth={Boolean(ZoomAppAuthService)}
                        hasEightByEightAppAuth={Boolean(
                            EightByEightAppAuthService,
                        )}
                        hasWebexAppAuth={Boolean(WebexAppAuthService)}
                        companyData={companyData}
                        companyAppAuth={resolveServiceAppAuth(service)}
                    />
                );
            case DIRECT_ROUTING:
                return (
                    <DashboardCard
                        key={uniqueKey}
                        service={service}
                        serviceWizard={serviceWizard}
                        msServiceAuth={msServiceAuth}
                        hasZoomAppAuth={Boolean(ZoomAppAuthService)}
                        hasEightByEightAppAuth={Boolean(
                            EightByEightAppAuthService,
                        )}
                        hasWebexAppAuth={Boolean(WebexAppAuthService)}
                        companyData={companyData}
                        companyAppAuth={resolveServiceAppAuth(service)}
                    />
                );
            case SBC:
                return <SBCCardDetails key={uniqueKey} service={service} />;
            case SRT:
                return <SRTCard key={uniqueKey} service={service} />;
            case VENDOR_DEFINED:
            case OPERATOR_PLATFORM:
                return (
                    <VendorCard
                        key={uniqueKey}
                        service={service}
                        serviceWizard={serviceWizard}
                    />
                );
            case GCC_HIGH:
                return (
                    <DashboardCard
                        key={uniqueKey}
                        service={service}
                        serviceWizard={serviceWizard}
                        msServiceAuth={msServiceAuth}
                        hasZoomAppAuth={Boolean(ZoomAppAuthService)}
                        hasEightByEightAppAuth={Boolean(
                            EightByEightAppAuthService,
                        )}
                        hasWebexAppAuth={Boolean(WebexAppAuthService)}
                        companyData={companyData}
                        companyAppAuth={resolveServiceAppAuth(service)}
                    />
                );
            default:
                return (
                    <DashboardCard
                        key={uniqueKey}
                        service={service}
                        serviceWizard={serviceWizard}
                        msServiceAuth={msServiceAuth}
                        hasZoomAppAuth={Boolean(ZoomAppAuthService)}
                        hasEightByEightAppAuth={Boolean(
                            EightByEightAppAuthService,
                        )}
                        hasWebexAppAuth={Boolean(WebexAppAuthService)}
                        companyData={companyData}
                    />
                );
        }
    };

    const cardStatus = useCallback(
        (service) => {
            return GetCardStatus(service, serviceStatus, darkMode);
        },
        [serviceStatus, darkMode],
    );

    const defaultIndex = useMemo(() => {
        const indices = [];
        Object.entries(groupedServices).forEach(
            ([categoryTitle, categoryData], idx) => {
                // The Microsoft tile always loads expanded, regardless of status.
                if (categoryTitle === MICROSOFT_CATEGORY_TITLE) {
                    indices.push(idx);
                    return;
                }

                const serviceColors = (categoryData?.services || []).map(
                    (svc) => cardStatus(svc, categoryTitle)?.color,
                );
                const authColors = (categoryData?.authServices || []).map(
                    (svc) =>
                        getAuthServiceCardStatus(
                            svc.serviceType,
                            svc.platformID,
                        )?.color,
                );

                const needsAttention = [...serviceColors, ...authColors].some(
                    (color) => ATTENTION_STATUS_COLORS.includes(color),
                );
                if (needsAttention) {
                    indices.push(idx);
                }
            },
        );
        return indices;
    }, [cardStatus, getAuthServiceCardStatus, groupedServices]);
    const allExpandedIndices = useMemo(
        () => Object.keys(groupedServices).map((_, idx) => idx),
        [groupedServices],
    );
    const categoryTitles = useMemo(
        () => Object.keys(groupedServices),
        [groupedServices],
    );

    useEffect(() => {
        setCachedExpandedTitles(
            readDashboardAccordionStateCache(
                currentCompany,
                accordionStateUser,
            ),
        );
    }, [accordionStateUser, currentCompany]);

    const cachedExpandedIndices = useMemo(() => {
        if (cachedExpandedTitles === null) return null;
        if (cachedExpandedTitles.length === 0) return [];

        return mapExpandedTitlesToIndices(cachedExpandedTitles, categoryTitles);
    }, [cachedExpandedTitles, categoryTitles]);
    const hasSkeletonServices = useMemo(
        () =>
            Object.values(groupedServices).some(
                (group) =>
                    group?.services?.some((service) => service?.__isSkeleton) ||
                    group?.authServices?.some(
                        (service) => service?.__isSkeleton,
                    ),
            ),
        [groupedServices],
    );

    const skeletonIndex = useMemo(() => {
        if (cachedExpandedIndices === null) return null;
        return cachedExpandedIndices.length ? cachedExpandedIndices : [];
    }, [cachedExpandedIndices]);

    useEffect(() => {
        setExpandedIndices((prev) => {
            if (hasSkeletonServices) {
                previousHadSkeletonRef.current = true;
                hasManualExpansionRef.current = false;
                return skeletonIndex ?? allExpandedIndices;
            }

            // When skeleton completes, snap to the data-driven default.
            if (previousHadSkeletonRef.current) {
                previousHadSkeletonRef.current = false;
                hasManualExpansionRef.current = false;
                shouldWriteCacheFromDefaultRef.current = true;
                return defaultIndex;
            }

            // First real render (no prior state): use the data-driven default.
            if (prev === null) {
                hasManualExpansionRef.current = false;
                shouldWriteCacheFromDefaultRef.current = true;
                return defaultIndex;
            }

            // If user has manually toggled, keep their choice within this session.
            if (hasManualExpansionRef.current) {
                const nextIndices = prev.filter((idx) =>
                    allExpandedIndices.includes(idx),
                );

                return nextIndices.length || prev.length === 0
                    ? nextIndices
                    : [];
            }

            // Otherwise stay purely data-driven.
            shouldWriteCacheFromDefaultRef.current = true;
            return defaultIndex;
        });
    }, [allExpandedIndices, defaultIndex, hasSkeletonServices, skeletonIndex]);

    useEffect(() => {
        if (hasSkeletonServices) return;
        if (!shouldWriteCacheFromDefaultRef.current) return;

        shouldWriteCacheFromDefaultRef.current = false;

        const titlesToCache = defaultIndex
            .map((idx) => categoryTitles[idx])
            .filter(Boolean);

        writeDashboardAccordionStateCache(
            currentCompany,
            accordionStateUser,
            titlesToCache,
        );
        setCachedExpandedTitles(titlesToCache);
    }, [
        accordionStateUser,
        categoryTitles,
        currentCompany,
        defaultIndex,
        hasSkeletonServices,
    ]);

    const handleAccordionChange = useCallback(
        (nextIndex) => {
            if (hasSkeletonServices) {
                return;
            }

            const normalizedIndex = Array.isArray(nextIndex)
                ? nextIndex
                : typeof nextIndex === 'number'
                  ? [nextIndex]
                  : [];

            hasManualExpansionRef.current = true;
            setExpandedIndices(normalizedIndex);
        },
        [hasSkeletonServices],
    );

    return (
        <Box mb={6} minWidth="600px">
            <Accordion
                index={expandedIndices ?? defaultIndex}
                onChange={handleAccordionChange}
                allowToggle
                allowMultiple
                reduceMotion
                borderRadius="8px"
                mb={4}>
                {Object.entries(groupedServices).map(
                    ([categoryTitle, categoryData], categoryIdx) => (
                        <AccordionItem
                            key={`${categoryTitle}-${categoryIdx}`}
                            sx={{
                                borderWidth: '1px',
                                borderStyle: 'solid',
                                borderColor: darkMode ? 'gray.700' : 'gray.200',
                                boxShadow: darkMode
                                    ? 'none'
                                    : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                            }}
                            borderRadius="8px"
                            marginBottom={4}
                            bg="transparent">
                            {({ isExpanded }) => {
                                const priorityService =
                                    categoryData?.statusPriority;
                                const currentCardStatus = cardStatus(
                                    priorityService,
                                    categoryTitle,
                                );

                                return (
                                    <>
                                        <HStack spacing={0} position="relative">
                                            <AccordionButton
                                                flex="1"
                                                borderRadius={
                                                    isExpanded
                                                        ? '8px 8px 0 0'
                                                        : '8px'
                                                }
                                                paddingTop={4}
                                                position="relative"
                                                height="61px"
                                                bg={
                                                    darkMode
                                                        ? 'dark.tcap'
                                                        : 'white'
                                                }
                                                _hover={{
                                                    bg: darkMode
                                                        ? 'blue.900'
                                                        : 'blue.50',
                                                }}
                                                paddingBottom={5}>
                                                {/* Progress Bar - Left Border */}
                                                {categoryData && (
                                                    <Progress
                                                        value={100}
                                                        sx={{
                                                            position:
                                                                'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            height: '100%',
                                                            width: '10px',
                                                            borderRadius:
                                                                isExpanded
                                                                    ? '8px 0 0 0'
                                                                    : '8px 0 0 8px',
                                                        }}
                                                        colorScheme={
                                                            currentCardStatus?.color
                                                        }
                                                        hasStripe={
                                                            currentCardStatus?.animation ||
                                                            currentCardStatus?.isAnimated
                                                        }
                                                        isAnimated={
                                                            currentCardStatus?.animation ||
                                                            currentCardStatus?.isAnimated
                                                        }
                                                    />
                                                )}
                                                <HStack
                                                    spacing={3}
                                                    flex="1"
                                                    textAlign="left">
                                                    {categoryData.icon && (
                                                        <Icon
                                                            as={
                                                                categoryData.icon
                                                            }
                                                            boxSize={6}
                                                            color={
                                                                darkMode
                                                                    ? 'white'
                                                                    : 'black'
                                                            }
                                                        />
                                                    )}
                                                    <HStack
                                                        aria-label="service accordion wrapper"
                                                        align="start"
                                                        spacing={3}
                                                        flex="1">
                                                        {/* Category Title */}
                                                        <Heading
                                                            size="md"
                                                            color={
                                                                darkMode
                                                                    ? 'white'
                                                                    : 'black'
                                                            }>
                                                            {categoryTitle ===
                                                            'Other'
                                                                ? 'Services without Authentication'
                                                                : categoryTitle}
                                                        </Heading>
                                                        {/* Service Count Badge */}
                                                        <Box
                                                            as="span"
                                                            fontSize="sm"
                                                            padding="2px 8px"
                                                            borderRadius="4px"
                                                            backgroundColor={
                                                                darkMode
                                                                    ? 'gray.400'
                                                                    : 'gray.200'
                                                            }
                                                            color={
                                                                darkMode
                                                                    ? 'white'
                                                                    : 'gray.700'
                                                            }>
                                                            {
                                                                categoryData
                                                                    .services
                                                                    .length
                                                            }
                                                        </Box>
                                                    </HStack>
                                                </HStack>
                                                {/* Expanded View - Service Icons */}
                                                {!isExpanded && (
                                                    <HStack
                                                        spacing={2}
                                                        as="span"
                                                        flex="0"
                                                        marginRight={8}
                                                        textAlign="right">
                                                        {categoryData.services.map(
                                                            (service, idx) => (
                                                                <IconPlatform
                                                                    key={`${categoryTitle}-icon-${getServiceKey(
                                                                        service,
                                                                        idx,
                                                                    )}`}
                                                                    service={
                                                                        service
                                                                    }
                                                                />
                                                            ),
                                                        )}
                                                    </HStack>
                                                )}
                                                <Box
                                                    as="span"
                                                    flex="0"
                                                    textAlign="right">
                                                    <AccordionIcon />
                                                </Box>
                                            </AccordionButton>
                                        </HStack>

                                        {isExpanded && (
                                            <AccordionPanel
                                                pb={4}
                                                px={0}
                                                borderRadius="0 0 8px 8px"
                                                backgroundColor={
                                                    darkMode
                                                        ? '#000000'
                                                        : 'white'
                                                }>
                                                {categoryData?.authServices[0]
                                                    ?.serviceType && (
                                                    <Box
                                                        width="100%"
                                                        px="20px"
                                                        pt="20px">
                                                        {categoryData
                                                            ?.authServices[0]
                                                            ?.__isSkeleton ? (
                                                            <DashboardHydrationCardSkeleton
                                                                service={
                                                                    categoryData
                                                                        ?.authServices[0]
                                                                }
                                                                animationDelayMs={
                                                                    categoryIdx *
                                                                    140
                                                                }
                                                            />
                                                        ) : (
                                                            <AppAuthCard
                                                                categoryData={
                                                                    categoryData
                                                                }
                                                                msServiceAuth={
                                                                    msServiceAuth
                                                                }
                                                                hasEightByEightAppAuth={Boolean(
                                                                    EightByEightAppAuthService,
                                                                )}
                                                                hasZoomAppAuth={Boolean(
                                                                    ZoomAppAuthService,
                                                                )}
                                                                hasWebexAppAuth={Boolean(
                                                                    WebexAppAuthService,
                                                                )}
                                                                serviceWizard={
                                                                    serviceWizard
                                                                }
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                                <Grid
                                                    margin="20px"
                                                    templateColumns="repeat(2, minmax(140px, 1fr))"
                                                    gap={4}>
                                                    {categoryData.services.map(
                                                        (service, idx) =>
                                                            renderServiceCard(
                                                                service,
                                                                `${categoryTitle}-card-${getServiceKey(service, idx)}`,
                                                                categoryIdx *
                                                                    140 +
                                                                    (idx + 1) *
                                                                        90,
                                                            ),
                                                    )}
                                                </Grid>
                                            </AccordionPanel>
                                        )}
                                    </>
                                );
                            }}
                        </AccordionItem>
                    ),
                )}
            </Accordion>
        </Box>
    );
};

export default DashboardAccordion;
